export const MOTION_PRESETS = [
  { id: "orbit-left", label: "Orbit left", category: "Interior", description: "Arcs anticlockwise around the room. Best for living areas, kitchens, and open-plan spaces.", prompt: "Very slow orbit-left arc around the room. Camera traces a smooth anticlockwise curve, gradually revealing the space from a shifting angle. Near furniture drifts past with natural parallax against the far wall. Steady, grounded pace. Constant speed from start to finish — no speeding up, no slowing down. No new objects appear." },
  { id: "orbit-right", label: "Orbit right", category: "Interior", description: "Arcs clockwise around the room. Best for living areas, kitchens, and open-plan spaces.", prompt: "Very slow orbit-right arc around the room. Camera traces a smooth clockwise curve, gradually revealing the space from a shifting angle. Near furniture drifts past with natural parallax against the far wall. Steady, grounded pace. Constant speed from start to finish — no speeding up, no slowing down. No new objects appear." },
  { id: "walk-forward", label: "Walk forward", category: "Interior", description: "Walks steadily into the room. Ideal for hallways, corridors, and entrance shots.", prompt: "Slow steadicam walk forward through the room at a calm, even pace. Doorframes and walls show natural parallax as the camera passes. Foreground surfaces drift to the edges of frame. Floating, tripod-level movement. Constant speed from start to finish — no speeding up, no slowing down. Nothing changes in the scene." },
  { id: "walk-back", label: "Walk back", category: "Interior", description: "Pulls back to reveal the full space. Great for showcasing room size and layout.", prompt: "Slow steadicam walk backward through the room, gently revealing the full space. Near surfaces and furniture drift toward camera as background holds steady. Calm, even reverse pace. Smooth, tripod-level movement. Constant speed from start to finish — no speeding up, no slowing down. Nothing changes in the scene." },
  { id: "pan-up", label: "Pan up", category: "Exterior", description: "Tilts upward from ground to roofline. Perfect for tall facades and period features.", prompt: "Slow vertical pan upward from ground level toward the roofline. The lower facade drifts out of frame as upper walls, windows and roof are gradually revealed. Smooth, tripod-mounted tilt. Steady, unhurried pace. Constant speed from start to finish — no speeding up, no slowing down. No new details are invented." },
  { id: "approach", label: "Approach", category: "Exterior", description: "Walks toward the property from the street. Best for front elevations and kerb appeal.", prompt: "Slow steadicam walk forward approaching the property. The building grows larger in frame as foreground path and landscaping show natural parallax. Grounded, walking pace. Smooth, floating movement at eye level. Constant speed from start to finish — no speeding up, no slowing down. No new details are invented." },
] as const

export const MOTION_CATEGORIES = [...new Set(MOTION_PRESETS.map((p) => p.category))] as const

export const RENDER_DIMS = {
  portrait: { w: 1080, h: 1920 },
  landscape: { w: 1920, h: 1080 },
  square: { w: 1080, h: 1080 },
} as const

export const ASPECT_CLASSES: Record<string, string> = {
  portrait: "aspect-[9/16]",
  landscape: "aspect-[16/9]",
  square: "aspect-square",
}

export const PREVIEW_CONTAINER_CLASSES: Record<string, string> = {
  portrait: "mx-auto max-w-[280px] aspect-[9/16]",
  landscape: "w-full aspect-video",
  square: "mx-auto max-w-[420px] aspect-square",
}

export const TEMPLATE_STYLES: Record<string, { scrimClass: string; textClass: string; headlineClass: string }> = {
  classic: {
    scrimClass: "bg-gradient-to-t from-charcoal/70 to-transparent",
    textClass: "font-display text-[13px] leading-snug text-white sm:text-[15px]",
    headlineClass: "font-display text-[17px] font-bold leading-snug text-white sm:text-[20px]",
  },
  modern: {
    scrimClass: "bg-gradient-to-t from-black/80 to-transparent",
    textClass: "font-sans text-[13px] font-bold uppercase leading-snug tracking-wide text-white sm:text-[15px]",
    headlineClass: "font-sans text-[17px] font-bold uppercase leading-snug tracking-wider text-white sm:text-[20px]",
  },
  luxury: {
    scrimClass: "bg-gradient-to-t from-charcoal/60 to-transparent",
    textClass: "font-display text-[13px] italic leading-snug text-white sm:text-[15px]",
    headlineClass: "font-display text-[17px] font-semibold italic leading-snug text-white sm:text-[20px]",
  },
  bold: {
    scrimClass: "bg-gradient-to-t from-black/80 to-transparent",
    textClass: "font-sans text-[13px] font-black uppercase leading-tight text-white sm:text-[15px]",
    headlineClass: "font-sans text-[17px] font-black uppercase leading-tight text-white sm:text-[20px]",
  },
}

export const FONT_OPTIONS = [
  { id: "playfair-display", label: "Playfair Display", family: "Playfair Display Variable" },
  { id: "dm-sans", label: "DM Sans", family: "DM Sans Variable" },
  { id: "inter", label: "Inter", family: "Inter" },
  { id: "montserrat", label: "Montserrat", family: "Montserrat" },
  { id: "poppins", label: "Poppins", family: "Poppins" },
  { id: "raleway", label: "Raleway", family: "Raleway" },
  { id: "open-sans", label: "Open Sans", family: "Open Sans" },
  { id: "lato", label: "Lato", family: "Lato" },
  { id: "work-sans", label: "Work Sans", family: "Work Sans" },
  { id: "oswald", label: "Oswald", family: "Oswald" },
  { id: "nunito", label: "Nunito", family: "Nunito" },
  { id: "merriweather", label: "Merriweather", family: "Merriweather" },
  { id: "libre-baskerville", label: "Libre Baskerville", family: "Libre Baskerville" },
  { id: "cormorant-garamond", label: "Cormorant Garamond", family: "Cormorant Garamond" },
  { id: "lora", label: "Lora", family: "Lora" },
] as const

export const FONT_WEIGHT_OPTIONS = [
  { id: "light", label: "Light", value: 300 },
  { id: "regular", label: "Regular", value: 400 },
  { id: "medium", label: "Medium", value: 500 },
  { id: "semibold", label: "Semibold", value: 600 },
  { id: "bold", label: "Bold", value: 700 },
  { id: "black", label: "Black", value: 900 },
] as const

export const COLOUR_PRESETS = [
  { id: "white", label: "White", value: "#FFFFFF" },
  { id: "charcoal", label: "Charcoal", value: "#1A1A1A" },
  { id: "red", label: "Red", value: "#C41E2A" },
  { id: "cream", label: "Cream", value: "#F5F2ED" },
] as const

export const SPECIAL_TRANSITION_OPTIONS = [
  { id: "iris", label: "Iris" },
  { id: "fade", label: "Fade" },
  { id: "slide", label: "Slide" },
  { id: "wipe", label: "Wipe" },
  { id: "flip", label: "Flip" },
  { id: "clock-wipe", label: "Clock" },
] as const

export type SpecialTransitionStyle = (typeof SPECIAL_TRANSITION_OPTIONS)[number]["id"]

export const DEFAULT_TITLE_CARD_DURATION = 2
export const MIN_CLIP_DURATION = 0.5
export const PX_PER_SECOND = 60
export const MAX_CONCURRENT_ANIMATIONS = 3

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toFixed(1).padStart(4, "0")}`
}

export function generateWaveformBars(sceneIndex: number, count = 24): number[] {
  const bars: number[] = []
  let seed = sceneIndex * 127 + 53
  for (let i = 0; i < count; i++) {
    seed = (seed * 16807 + 13) % 2147483647
    bars.push(0.25 + (seed % 1000) / 1000 * 0.65)
  }
  return bars
}

export type TextPosition = "top" | "middle" | "bottom"
export type TextAlign = "left" | "center" | "right"
export type LogoSize = "small" | "medium" | "large"

export const LOGO_SIZE_OPTIONS: Array<{ id: LogoSize; label: string; scale: number }> = [
  { id: "small", label: "Small", scale: 1 },
  { id: "medium", label: "Medium", scale: 1.5 },
  { id: "large", label: "Large", scale: 2 },
]

export const FONT_SIZE_OPTIONS = [
  { id: "small", label: "Small", remotionPx: 36, previewClass: "text-[11px] sm:text-[13px]" },
  { id: "medium", label: "Medium", remotionPx: 48, previewClass: "text-[13px] sm:text-[15px]" },
  { id: "large", label: "Large", remotionPx: 64, previewClass: "text-[15px] sm:text-[18px]" },
  { id: "xl", label: "XL", remotionPx: 80, previewClass: "text-[18px] sm:text-[22px]" },
] as const

export type ScriptContent = {
  headline?: string
  title_narration?: string
  closing_narration?: string
  scenes?: Array<{
    text: string
    duration_seconds: number
    type?: "closing"
    text_position?: TextPosition
    text_align?: TextAlign
    text_font?: string
    text_size?: string
    text_colour?: string
    text_uppercase?: boolean
    text_weight?: string
    show_cta?: boolean
    show_cta_button?: boolean
  }>
  narration?: string
  cta_text?: string
  cta_button_text_colour?: string
  title_card_image_index?: number
  title_card_bg_colour?: string | null
  title_card_duration?: number
  title_show_branding?: boolean
  title_bg_gradient?: "none" | "linear-top" | "linear-bottom" | "radial"
  title_bg_gradient_end?: string
  title_logo_position?: "above" | "below" | "hidden"
  title_logo_size?: LogoSize
  title_transition_style?: "iris" | "fade" | "slide" | "wipe" | "flip" | "clock-wipe"
  // Title card text styling
  title_text_position?: TextPosition
  title_text_align?: TextAlign
  title_text_font?: string
  title_text_size?: string
  title_text_colour?: string
  title_text_uppercase?: boolean
  title_text_weight?: string
  // Closing scene styling
  closing_bg_colour?: string | null
  closing_bg_gradient?: "none" | "linear-top" | "linear-bottom" | "radial"
  closing_bg_gradient_end?: string
  closing_transition_style?: "iris" | "fade" | "slide" | "wipe" | "flip" | "clock-wipe"
  closing_text_font?: string
  closing_text_size?: string
  closing_text_colour?: string
  closing_text_uppercase?: boolean
  closing_text_weight?: string
  closing_logo_size?: LogoSize
  show_watermark?: boolean
}

// Google Fonts URL for loading fonts in frontend preview
export const GOOGLE_FONTS_URL = `https://fonts.googleapis.com/css2?${FONT_OPTIONS.map((f) => `family=${encodeURIComponent(f.family)}:wght@300;400;500;600;700;900`).join("&")}&display=swap`

export type ClipLayout = {
  index: number // -1 = title, 0..n = content scenes, last = closing
  type: "title" | "content" | "closing"
  left: number // px offset
  width: number // px width
  duration: number // seconds
  startTime: number // seconds
}

export function computeClipLayouts(
  content: ScriptContent | undefined,
  pxPerSec: number,
): ClipLayout[] {
  if (!content?.scenes) return []

  const titleDuration = content.title_card_duration ?? DEFAULT_TITLE_CARD_DURATION
  const contentScenes = content.scenes.filter((s) => s.type !== "closing")
  const closingScene = content.scenes.find((s) => s.type === "closing")
  const closingDuration = closingScene?.duration_seconds ?? 4

  const layouts: ClipLayout[] = []
  let time = 0

  // Title card
  layouts.push({
    index: -1,
    type: "title",
    left: time * pxPerSec,
    width: Math.max(40, titleDuration * pxPerSec),
    duration: titleDuration,
    startTime: time,
  })
  time += titleDuration

  // Content scenes
  for (let i = 0; i < contentScenes.length; i++) {
    const dur = contentScenes[i].duration_seconds
    layouts.push({
      index: i,
      type: "content",
      left: time * pxPerSec,
      width: Math.max(40, dur * pxPerSec),
      duration: dur,
      startTime: time,
    })
    time += dur
  }

  // Closing
  layouts.push({
    index: contentScenes.length,
    type: "closing",
    left: time * pxPerSec,
    width: Math.max(40, closingDuration * pxPerSec),
    duration: closingDuration,
    startTime: time,
  })

  return layouts
}